import { Info, FileText } from "lucide-react";
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Container,
  Space,
  TextInput,
  Text,
  Group,
  useMantineTheme,
  Checkbox,
  createStyles,
  CheckboxGroup,
  Stack,
  Title,
  NumberInput,
  Alert,
  Progress,
  Switch,
} from "@mantine/core";
import { MIME_TYPES } from "@mantine/dropzone";
import { Navigate } from "react-router-dom";
import { DatePicker, TimeInput } from "@mantine/dates";
import dayjs from "dayjs";
import axios, { AxiosError } from "axios";
import { useFocusWithin } from "@mantine/hooks";

import { putToSignedUrlWithRetry } from "../utils/signedUrlUpload";
import { AuthContext } from "../../../providers/AuthProvider";
import FileUpload, {
  FileRejection,
} from "../../../themes/components/FileUpload";
import ButtonProgress from "../../../themes/components/ButtonProgress";
import { queryClient } from "../../../App";
import { HttpClientError } from "../../../providers/HttpClient";
import useUploadPayslipDocumentApiService from "../services/api/UploadPayslipDocumentApiService";
import useConfirmDocumentUploaded from "../services/api/ConfirmDocumentUploaded";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import useFeatureConfigurationApiService from "../services/api/FeatureConfigurationApiService";
import {
  FeatureConfiguration,
  SupportedDocumentAttributes,
  SupportedFeatures,
} from "../services/model/FeatureConfiguration";
import DocumentImporterConfiguration from "../services/model/DocumentImporterConfiguration";
import InputLabel from "../../../common/elements/InputLabel";
import Dictionary from "../../../utils/Dictionary";
import CustomTooltip from "../../../common/elements/CustomTooltip";
import FeatureProtectedComponent from "../../../common/uac/FeatureProtectedComponent";
import UserAccessControlUtils, {
  FEATURE_SELF_MANAGED_PAYSLIPS_MATCHING_CRITERIA,
} from "../../../utils/UserAccessControlUtils";

import DocumentProcessingJob from "../services/model/DocumentProcessingJob";
import { useDocumentUploadBatchNameListApiService } from "../services/api/DocumentBatchNameListApiService";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import { MessageTypeTrigger } from "../../../providers/notifications/types";
import PayslipUploadModeToggle, {
  PayslipUploadMode,
} from "./PayslipUploadModeToggle";
import ZipPayslipNoMatchingTip from "./ZipPayslipNoMatchingTip";
import useRequestZipPayslipUploadApiService from "../services/api/RequestZipPayslipUploadApiService";
import useConfirmZipPayslipUploadApiService from "../services/api/ConfirmZipPayslipUploadApiService";

const useStyles = createStyles((theme) => ({
  btnWrapper: {
    boxSizing: "border-box",
    width: "100%",
    [`@media (max-width: ${theme.breakpoints.xs}px)`]: {
      padding: "0",
      position: "initial",
    },
  },
}));

type PayslipUploadFormProps = {
  onSuccess?: () => void;
  onFailure?: () => void;
  setExitingUpload?: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadingClicked: React.Dispatch<React.SetStateAction<boolean>>;
  documentImporterConfiguration: DocumentImporterConfiguration;
};

const PayslipUploadForm: React.FC<PayslipUploadFormProps> = ({
  onSuccess,
  onFailure,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setExitingUpload,
  setUploadingClicked,
  documentImporterConfiguration,
}) => {
  const auth = useContext(AuthContext);
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);
  const theme = useMantineTheme();
  const { ref, focused } = useFocusWithin();
  const { showToast } = useToasts();
  const { classes } = useStyles();

  // All useState hooks grouped together
  const [mode, setMode] = useState<PayslipUploadMode>("pdf");
  const [batchName, setBatchName] = useState<string>("");
  const [issueDate, setIssueDate] = useState<Date | null>(null);
  const [distributionDate, setDistributionDate] = useState<Date | null>(null);
  const [shouldSendNotifications, setShouldSendNotifications] =
    useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploadingToSignedUrl, setIsUploadingToSignedUrl] =
    useState<boolean>(false);
  const [file, setFile] = useState<File | undefined>();
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [batchNameError, setBatchNameError] = useState<string>("");
  const [documentMatchingCriteria, setDocumentMatchingCriteria] =
    useState<FeatureConfiguration>();
  const [selectedMatchCriteria, setSelectedMatchCriteria] =
    useState<FeatureConfiguration>();
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const allEmployerDocumentsResponse = useDocumentUploadBatchNameListApiService(
    {
      employerId: auth?.employer?.id || 0,
    }
  );

  const uniqueBatchNames = useMemo(() => {
    if (allEmployerDocumentsResponse?.data) {
      return allEmployerDocumentsResponse.data.map(
        (docJob: DocumentProcessingJob) => docJob.batch_name
      );
    }
    return [];
  }, [allEmployerDocumentsResponse?.data]);

  const { data, isLoading, mutate, isError, error, isSuccess } =
    useUploadPayslipDocumentApiService();

  const zipInit = useRequestZipPayslipUploadApiService();
  const zipConfirm = useConfirmZipPayslipUploadApiService();
  // Tracks which job_id we've already started the ZIP PUT for, so the effect
  // that triggers the upload doesn't re-fire when other dependencies change.
  const zipUploadStartedRef = useRef<number | null>(null);

  // ZIP mode is only wired end-to-end for Payslips today. The Cloud Run Job
  // pipeline hardcodes payslip extraction/insert, and the monolith endpoint
  // forces document_type="Payslips" on the resulting DocumentProcessingJob.
  // Showing the toggle on Rosters/Timesheets/IRP5s would let users upload
  // ZIPs that land in the payslip table — corrupt data. Hide until the
  // backend supports each type explicitly.
  const zipModeAvailable =
    documentImporterConfiguration.document_type === SupportedFeatures.PAYSLIPS;

  // Defensive: if the form is mounted for a non-Payslips document type while
  // ZIP mode happens to be selected (e.g., from a previous render), snap back
  // to PDF and clear the staged file/progress so the submit handler can't
  // accidentally branch into the zip path. The submit handler itself also
  // gates on zipModeAvailable.
  useEffect(() => {
    if (zipModeAvailable) return;
    if (mode !== "pdf") {
      setMode("pdf");
      setFile(undefined);
      setUploadProgress(0);
    }
  }, [zipModeAvailable, mode]);

  const matchingCriteriaService = useFeatureConfigurationApiService(
    documentImporterConfiguration?.document_type || ""
  );

  const {
    mutate: documentUploadedMutate,
    isError: documentUploadedIsError,
    error: documentUploadedError,
  } = useConfirmDocumentUploaded();

  const flagPayslipNotificationsSendOption = useEnabledFeatureFlag(
    FeatureFlags.PAYSLIP_NOTIFICATIONS_SEND_OPTION
  );
  const flagIrp5pNotificationsSendOption = useEnabledFeatureFlag(
    FeatureFlags.IRP5_NOTIFICATIONS_SEND_OPTION
  );

  const flagRosterTimesheetNotificationsSendOption = useEnabledFeatureFlag(
    FeatureFlags.ROSTER_TIMESHEET_NOTIFICATIONS_SEND_OPTION
  );

  const matchingCriteriaDictionary: { [key: string]: string } = {
    id_number: "ID Number",
    employee_no: "Employee Number",
    bank_account_number: "Bank Account Number",
    first_name: "First Name",
    last_name: "Last Name",
  };

  const maxSizeEnv: string =
    process.env.REACT_APP_MAX_PAYSLIP_FILE_SIZE_MB || "50";
  const maxSizeInMb: number = parseInt(maxSizeEnv, 10);
  const maxSizeInBytes = maxSizeInMb * 1024 ** 2;

  const showRejectionErrors = (fileRejections: FileRejection[]): void => {
    fileRejections.forEach((fileRejection) => {
      let errorMessage = "";
      fileRejection.errors.forEach((fileError, index) => {
        let { message } = fileError;
        if (fileError.code === "file-too-large") {
          message = `File too large, maximum file size is ${
            maxSizeInBytes / 1024 ** 2
          } MB, Consider splitting the file into 32mb chunks`;
        }
        errorMessage +=
          message + (index < fileRejection.errors.length - 1 ? ", " : "");
      });
      showToast({
        strapiId: "document_upload_error",
        data: {
          title: "File upload failed!",
          message: errorMessage,
        },
      });
    });
  };
  const suggestBatchName = (currentBatchName: string, n: number): string => {
    const newSuggestedBatchName = `${currentBatchName}-${n}`;
    if (!uniqueBatchNames.includes(newSuggestedBatchName)) {
      return newSuggestedBatchName;
    }
    return suggestBatchName(currentBatchName, n + 1);
  };

  const getSignedUrl = useCallback((): void => {
    // Early returns for all required fields
    if (!auth?.employer?.id || !auth.id) return;
    if (!batchName) return;
    if (!issueDate) return;
    if (!distributionDate) return;
    if (!file) return;

    if (
      UserAccessControlUtils.hasFeature(
        auth?.user_features || [],
        FEATURE_SELF_MANAGED_PAYSLIPS_MATCHING_CRITERIA
      ) &&
      !selectedMatchCriteria?.matching_criteria &&
      !selectedMatchCriteria?.document_length
    ) {
      return;
    }

    if (
      documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.START_AND_END_PERIOD
      ) &&
      !(startDate && endDate)
    ) {
      return;
    }

    // Determine if we should include the 'extra_attributes' field
    const includeExtraAttributes =
      documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.START_AND_END_PERIOD
      );

    // Prepare the extra_attributes object conditionally
    const extraAttributesObject = includeExtraAttributes
      ? {
          extra_attributes: JSON.stringify({
            start_date: dayjs(startDate).format("YYYY-MM-DD"),
            end_date: dayjs(endDate).format("YYYY-MM-DD"),
          }),
        }
      : {};

    mutate({
      employer: auth.employer.id,
      uploaded_by: auth.id,
      batch_name: batchName,
      should_send_notifications: shouldSendNotifications,
      date_issued: dayjs(issueDate).format("YYYY-MM-DD"),
      date_of_distribution: dayjs
        .utc(distributionDate)
        .format("YYYY-MM-DDTHH:mm:ss"),
      document_type: documentImporterConfiguration.document_type,
      configuration: JSON.stringify(selectedMatchCriteria, null, 2),
      configuration_override:
        selectedMatchCriteria !== documentMatchingCriteria,
      ...extraAttributesObject,
    });
  }, [
    auth,
    batchName,
    issueDate,
    distributionDate,
    file,
    startDate,
    endDate,
    selectedMatchCriteria,
    documentMatchingCriteria,
    documentImporterConfiguration,
    shouldSendNotifications,
  ]);

  const uploadFile = useCallback((): void => {
    if (!data) return;
    putToSignedUrlWithRetry(
      data.signed_url,
      file,
      {
        headers: {
          "Content-Type": "application/pdf",
          "X-Goog-Content-Length-Range": `${data?.min_size},${data?.max_size}`,
        },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          const percentCompleted = Math.round((loaded * 100) / total);
          setUploadProgress(percentCompleted);
        },
      },
      {
        // Transient GCS/network failure (ENG-4597): reset the progress bar so
        // the user sees the attempt restart rather than a stuck bar.
        onRetry: () => setUploadProgress(0),
      }
    )
      .then(async () => {
        try {
          await documentUploadedMutate({ jobId: data.id, succeeded: true });
          trackDocumentsEvent(
            {
              event: `Select_File_CTA_Uploaded`,
              description:
                "User has successfully selected and uploaded a file in the upload view",
              event_type: "CTA:Click",
              type: documentImporterConfiguration.document_name_plural,
              job: data || null,
            },
            {
              batch_name: batchName,
              notification_sent_status: shouldSendNotifications,
              file_uploaded: true,
              document_status: "uploaded",
            }
          );
        } catch (uploadError: any) {
          showToast({
            strapiId: "document_upload_error",
            data: {
              title: "File confirmation failed!",
              message: uploadError.message,
            },
          });
        }

        showToast({
          toast: { color: "rgba(16, 122, 84, 1)", autoClose: 4000 },
          resourceType: documentImporterConfiguration.document_type,
          trigger: MessageTypeTrigger.UploadSuccess,
          data,
        });

        if (onSuccess) {
          onSuccess();
        }
      })
      .catch(async () => {
        await documentUploadedMutate({ jobId: data.id, succeeded: false });
        showToast({
          toast: { color: "rgba(234, 70, 67, 1)", autoClose: 4000 },
          resourceType: documentImporterConfiguration.document_type,
          trigger: MessageTypeTrigger.UploadFailure,
          data,
        });
        if (onFailure) {
          onFailure();
        }
      });
  }, [
    data,
    file,
    documentImporterConfiguration,
    onSuccess,
    onFailure,
    documentUploadedMutate,
    showToast,
    batchName,
    shouldSendNotifications,
    trackDocumentsEvent,
  ]);

  // Upload effect: inlined so we can hold the AbortController in closure. Fires
  // exactly once per init response thanks to zipUploadStartedRef, and aborts the
  // in-flight PUT if the component unmounts or the file changes mid-upload.
  //
  // Dep-list rationale: [mode, zipInit.data, file] are the only inputs that
  // should re-trigger this effect. Other closure values (batchName,
  // shouldSendNotifications, documentImporterConfiguration, the various
  // callbacks/showToast) are intentionally omitted — re-running the upload on
  // every keystroke would either restart the PUT or fire it twice for the same
  // init response. The zipUploadStartedRef latch keyed on init.job_id makes the
  // "fires exactly once per init" guarantee explicit, so the stale closure on
  // those omitted values is acceptable for this single-shot operation.
  //
  // `return undefined;` (not bare `return;`) is required because the cleanup
  // path returns a function — `consistent-return` enforces all-or-nothing.
  useEffect(() => {
    if (!zipModeAvailable) return undefined;
    if (mode !== "zip") return undefined;
    const init = zipInit.data;
    if (!init || !file) return undefined;
    if (zipUploadStartedRef.current === init.job_id) return undefined;
    zipUploadStartedRef.current = init.job_id;

    const controller = new AbortController();
    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        await queryClient.invalidateQueries(["DocumentUploadListApiService"]);
        if (cancelled) return;
        setIsUploadingToSignedUrl(true);
        await putToSignedUrlWithRetry(
          init.signed_url,
          file,
          {
            signal: controller.signal,
            headers: {
              "Content-Type": init.content_type,
              "X-Goog-Content-Length-Range": `${init.min_size},${init.max_size}`,
            },
            onUploadProgress: (progressEvent) => {
              if (cancelled) return;
              const { loaded, total } = progressEvent;
              setUploadProgress(Math.round((loaded * 100) / total));
            },
          },
          {
            // Transient GCS/network failure (ENG-4597): reset the progress bar
            // so the user sees the attempt restart rather than a stuck bar.
            // Aborts are terminal inside the helper, so a cancelled effect
            // never spins extra attempts.
            onRetry: () => {
              if (!cancelled) setUploadProgress(0);
            },
          }
        );
        setIsUploadingToSignedUrl(false);
        if (cancelled) return;

        try {
          await zipConfirm.mutateAsync({ jobId: init.job_id });
        } catch {
          // The zipConfirm.isError useEffect (with zipConfirmErrorShownRef
          // latch + zipConfirm.reset()) handles the toast + onFailure + state
          // cleanup uniformly. Just bail out of the rest of the upload flow
          // here so we don't fire the success toast/track event below.
          return;
        }

        if (cancelled) return;

        showToast({
          toast: { color: "rgba(16, 122, 84, 1)", autoClose: 4000 },
          resourceType: documentImporterConfiguration.document_type,
          trigger: MessageTypeTrigger.UploadSuccess,
          data: { ...init, batch_name: batchName },
        });

        trackDocumentsEvent(
          {
            event: `Select_File_CTA_Uploaded`,
            description: "User has successfully uploaded a ZIP batch",
            event_type: "CTA:Click",
            type: documentImporterConfiguration.document_name_plural,
            job: null,
          },
          {
            batch_name: batchName,
            notification_sent_status: shouldSendNotifications,
            file_uploaded: true,
            document_status: "uploaded",
            upload_mode: "zip",
          }
        );

        if (onSuccess) onSuccess();
      } catch (err) {
        setIsUploadingToSignedUrl(false);
        if (cancelled || axios.isCancel(err)) return;
        showToast({
          toast: { color: "rgba(234, 70, 67, 1)", autoClose: 4000 },
          resourceType: documentImporterConfiguration.document_type,
          trigger: MessageTypeTrigger.UploadFailure,
          data: { ...init, batch_name: batchName },
        });
        if (onFailure) onFailure();
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
      setIsUploadingToSignedUrl(false);
    };
  }, [zipModeAvailable, mode, zipInit.data, file]);

  // All useEffect hooks grouped together
  useEffect(() => {
    if (!isError) return;
    const errorResponse = error.response?.data;
    const showErrorToast = (message: string): void => {
      showToast({
        strapiId: "document_upload_error",
        data: {
          title: `${documentImporterConfiguration.document_name} upload failed!`,
          message,
        },
      });
    };
    if (Array.isArray(errorResponse)) {
      errorResponse.forEach(showErrorToast);
    } else {
      showErrorToast(HttpClientError(error));
    }
    onFailure?.();
  }, [isError, error, documentImporterConfiguration, onFailure]);

  useEffect(() => {
    if (isSuccess) {
      queryClient
        .invalidateQueries(["DocumentUploadListApiService"])
        .then(() => {
          uploadFile();
        });
    }
  }, [isSuccess]);

  useEffect(() => {
    // Reset the ref so a fresh upload can start after mode switch or file removal.
    zipUploadStartedRef.current = null;
  }, [mode, file]);

  // Track the AxiosError reference we've already shown the toast for so the
  // effect never fires twice for the same error. Without this, every parent
  // re-render (e.g. caused by onFailure setting state) re-enters the effect
  // while zipInit.isError is still true and we spam the toast + console.
  const zipInitErrorShownRef = useRef<unknown>(null);
  useEffect(() => {
    if (!zipInit.isError) {
      zipInitErrorShownRef.current = null;
      return;
    }
    if (zipInitErrorShownRef.current === zipInit.error) return;
    zipInitErrorShownRef.current = zipInit.error;

    const message = HttpClientError(zipInit.error as AxiosError);
    showToast({
      strapiId: "document_upload_error",
      data: {
        title: `${documentImporterConfiguration.document_name} upload failed!`,
        message,
      },
    });
    if (onFailure) onFailure();
    // Clear the error from react-query so a subsequent retry isn't blocked
    // and the effect's `isError` guard short-circuits on the next render.
    zipInit.reset();
    // The ref latch above protects against re-fires when other deps change,
    // so the tight dep list ([isError, error]) is intentional.
  }, [zipInit.isError, zipInit.error]);

  // Same latch + reset pattern for the confirm mutation: a 4xx/5xx from
  // /confirm/ must not loop.
  const zipConfirmErrorShownRef = useRef<unknown>(null);
  useEffect(() => {
    if (!zipConfirm.isError) {
      zipConfirmErrorShownRef.current = null;
      return;
    }
    if (zipConfirmErrorShownRef.current === zipConfirm.error) return;
    zipConfirmErrorShownRef.current = zipConfirm.error;

    showToast({
      strapiId: "document_upload_error",
      data: {
        title: "Batch confirmation failed!",
        message: HttpClientError(zipConfirm.error as AxiosError),
      },
    });
    if (onFailure) onFailure();
    zipConfirm.reset();
  }, [zipConfirm.isError, zipConfirm.error]);
  useEffect(() => {
    if (!focused) {
      if (batchName && uniqueBatchNames.includes(batchName)) {
        const newBatchName = suggestBatchName(batchName, 1);
        setBatchNameError(
          `Batch name is already in use. Please use a unique name for your payslip batch. Try ${newBatchName}`
        );
      } else {
        setBatchNameError("");
      }
    }
  }, [focused, batchName, uniqueBatchNames]);

  useEffect(() => {
    const shouldSetDefaultBatchName =
      !documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.BATCH_NAME
      );

    if (shouldSetDefaultBatchName && startDate && endDate) {
      const startMonth = startDate.toLocaleString("default", { month: "long" });
      const endMonth = endDate.toLocaleString("default", { month: "long" });
      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();
      const epoch = new Date().getTime();

      let batchNameValue = "";
      if (startMonth === endMonth && startYear === endYear) {
        batchNameValue = `${startMonth} ${startYear} Batch (${epoch})`;
      } else if (startYear === endYear) {
        batchNameValue = `${startMonth} to ${endMonth} ${startYear} Batch (${epoch})`;
      } else {
        batchNameValue = `${startMonth} ${startYear} - ${endMonth} ${endYear} Batch (${epoch})`;
      }

      setBatchName(batchNameValue);
    }
  }, [documentImporterConfiguration.attributes, startDate, endDate]);
  useEffect(() => {
    if (
      !documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.ISSUE_DATE
      )
    ) {
      const defaultIssueDate = new Date();
      setIssueDate(defaultIssueDate);
    }
  }, [documentImporterConfiguration.attributes]);

  useEffect(() => {
    if (matchingCriteriaService?.data) {
      setDocumentMatchingCriteria(matchingCriteriaService.data.configuration);
      setSelectedMatchCriteria(matchingCriteriaService.data.configuration);
    }
  }, [matchingCriteriaService?.data]);

  useEffect(() => {
    if (documentUploadedIsError) {
      showToast({
        strapiId: "document_upload_error",
        data: {
          title: "Document upload confirmation failed!",
          message: HttpClientError(documentUploadedError),
        },
      });

      if (onFailure) {
        onFailure();
      }
    }
  }, [documentUploadedIsError, documentUploadedError, onFailure, showToast]);

  const updateSelectedMatchingCriteria = useCallback((value: string[]) => {
    const newMatchingCriteria = value.toString();
    setSelectedMatchCriteria((prev) => ({
      document_length: prev?.document_length || 1,
      matching_criteria: newMatchingCriteria,
    }));
  }, []);

  const updateSelectedMatchingDocumentLength = useCallback(
    (value: number | undefined) => {
      setSelectedMatchCriteria((prev) => ({
        document_length:
          value && value > 0 && Number.isInteger(value)
            ? value
            : prev?.document_length || 1,
        matching_criteria: prev?.matching_criteria || "",
      }));
    },
    []
  );
  const setStartDateAndDefaultEndDate = useCallback(
    (date: Date) => {
      setStartDate(date);
      if (!endDate) {
        const newEndDate = new Date(date);
        newEndDate.setMonth(newEndDate.getMonth() + 1);
        newEndDate.setDate(newEndDate.getDate() - 1);
        setEndDate(newEndDate);
      }
    },
    [endDate]
  );

  // Auth check moved after all hooks
  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  const canUpload = (): boolean => {
    if (!file) return false;
    if (!batchName) return false;
    if (!issueDate) return false;
    if (!distributionDate) return false;
    if (!auth) return false;
    if (batchNameError.length > 0) return false;

    if (mode === "zip") {
      // ZIP mode skips matching criteria entirely (matching is filename-based,
      // handled server-side by the Cloud Run Job). FileUpload restricts MIME types,
      // but a renamed file could slip through — reject anything not ending in .zip.
      return file.name.toLowerCase().endsWith(".zip");
    }

    if (
      UserAccessControlUtils.hasFeature(
        auth?.user_features || [],
        FEATURE_SELF_MANAGED_PAYSLIPS_MATCHING_CRITERIA
      ) &&
      !selectedMatchCriteria?.matching_criteria &&
      !selectedMatchCriteria?.document_length
    ) {
      return false;
    }

    if (
      documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.START_AND_END_PERIOD
      )
    ) {
      if (!(startDate && endDate)) {
        return false;
      }
    }

    return true;
  };
  const showNotificationCheckBox =
    (flagPayslipNotificationsSendOption &&
      documentImporterConfiguration.document_type ===
        SupportedFeatures.PAYSLIPS) ||
    (flagIrp5pNotificationsSendOption &&
      documentImporterConfiguration.document_type ===
        SupportedFeatures.IRP5s) ||
    (flagRosterTimesheetNotificationsSendOption &&
      documentImporterConfiguration.document_type ===
        SupportedFeatures.ROSTERS) ||
    (flagRosterTimesheetNotificationsSendOption &&
      documentImporterConfiguration.document_type ===
        SupportedFeatures.TIMESHEETS);

  const description = (
    <Text align="center" size="sm" color="dimmed">
      {mode === "zip"
        ? "Drag and drop a ZIP archive here or select to upload. Files must be less than 1 GB in size."
        : `Drag and drop files here or select to upload. Files must be less than ${maxSizeInMb}mb in size.`}
    </Text>
  );

  return (
    <Container className="px-10 py-4">
      <Space h="md" />
      {zipModeAvailable && (
        <>
          <PayslipUploadModeToggle
            value={mode}
            onChange={(next) => {
              if (next === mode) return;
              setMode(next);
              setFile(undefined);
              setUploadProgress(0);
            }}
            disabled={
              isLoading ||
              zipInit.isLoading ||
              zipConfirm.isLoading ||
              isUploadingToSignedUrl
            }
          />
          <Space h="md" />
        </>
      )}
      <FileUpload
        isLoading={
          isLoading ||
          zipInit.isLoading ||
          zipConfirm.isLoading ||
          isUploadingToSignedUrl
        }
        onDrop={(files) => {
          trackDocumentsEvent(
            {
              event: `Select_File_CTA`,
              description:
                "User clicks on the upload file button on the upload view",
              event_type: "CTA:Click",
              type: documentImporterConfiguration.document_name_plural,
              job: data || null,
            },
            {
              batch_name: batchName,
              notification_sent_status: shouldSendNotifications,
              file_uploaded: false,
            }
          );
          setFile(files[0]);
        }}
        onRemove={() => {
          trackDocumentsEvent(
            {
              event: `Remove_File_CTA`,
              description:
                "User clicks on the remove file button on the upload view",
              event_type: "CTA:Click",
              type: documentImporterConfiguration.document_name_plural,
              job: data || null,
            },
            {
              batch_name: batchName,
              notification_sent_status: shouldSendNotifications,
            }
          );
          setFile(undefined);
        }}
        file={file}
        onReject={(files) => showRejectionErrors(files)}
        maxSize={mode === "zip" ? 1024 ** 3 : maxSizeInBytes}
        accept={mode === "zip" ? [MIME_TYPES.zip] : [MIME_TYPES.pdf]}
        getStatus={(status) => {
          if (status.accepted) {
            return "Drop files here";
          }

          if (status.rejected) {
            return mode === "zip"
              ? "File must be less than 1 GB"
              : `File must be less than ${maxSizeInMb}mb`;
          }
          return "Upload file";
        }}
        description={description}
      />
      {file && (
        <>
          <Text style={{ fontWeight: 700 }}>Upload Files</Text>
          <Stack
            style={{ flexDirection: "row", padding: "20px 0" }}
            align="center"
          >
            <FileText size={28} />
            <Stack style={{ flexGrow: 1 }} spacing={1}>
              <Stack style={{ flexDirection: "row" }} justify="space-between">
                <Text>{file.name}</Text>
                <Text>{(file.size / (1024 * 1024)).toFixed(2)}MB</Text>
              </Stack>
              <Progress value={uploadProgress} />
            </Stack>
          </Stack>
        </>
      )}

      {file && mode === "zip" && (
        <>
          <ZipPayslipNoMatchingTip />
          <Space h="sm" />
        </>
      )}

      {documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.BATCH_NAME
      ) && (
        <TextInput
          ref={ref}
          id="payslips-modal-batchname-input"
          label={
            <InputLabel
              label={
                Dictionary[documentImporterConfiguration.document_type]
                  .batch_name
              }
              tooltip={
                Dictionary[documentImporterConfiguration.document_type]
                  .batch_name_tooltip
              }
            />
          }
          required
          placeholder="Enter a name for this Batch"
          onChange={(value) => setBatchName(value.target.value)}
          error={batchNameError}
        />
      )}

      <Space h="sm" />

      {documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.START_AND_END_PERIOD
      ) && (
        <Stack spacing="xs">
          <InputLabel
            label={
              Dictionary[documentImporterConfiguration.document_type]
                .period_dates_label
            }
            tooltip={
              Dictionary[documentImporterConfiguration.document_type]
                .period_dates_tooltip
            }
            textStyle={{ fontWeight: 500 }}
          />
          <Group>
            <DatePicker
              placeholder="Start date"
              required
              value={startDate}
              onChange={(value: Date) => setStartDateAndDefaultEndDate(value)}
              maxDate={endDate}
              clearable={false}
              dayStyle={(date) =>
                date.getTime() === new Date().setHours(0, 0, 0, 0) && !startDate
                  ? {
                      backgroundColor: theme.colors[theme.primaryColor][6],
                      color: "white",
                    }
                  : {}
              }
            />
            <Space h="sm" />
            <DatePicker
              placeholder="End date"
              required
              value={endDate}
              onChange={(value: Date) => setEndDate(value)}
              minDate={startDate}
              clearable={false}
              disabled={!startDate}
              dayStyle={(date) =>
                date.getTime() === new Date().setHours(0, 0, 0, 0) && !endDate
                  ? {
                      backgroundColor: theme.colors[theme.primaryColor][6],
                      color: "white",
                    }
                  : {}
              }
            />
          </Group>
        </Stack>
      )}

      <Space h="sm" />

      {documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.ISSUE_DATE
      ) && (
        <DatePicker
          id="payslips-modal-issuedate-input"
          placeholder="Select an issue date"
          label={
            <InputLabel
              label={
                Dictionary[documentImporterConfiguration.document_type]
                  .issue_date
              }
              tooltip={
                Dictionary[documentImporterConfiguration.document_type]
                  .issue_date_tooltip
              }
            />
          }
          required
          dayStyle={(date) =>
            date.getTime() === new Date().setHours(0, 0, 0, 0) && !issueDate
              ? {
                  backgroundColor: theme.colors[theme.primaryColor][6],
                  color: "white",
                }
              : {}
          }
          onChange={(value) => setIssueDate(value)}
        />
      )}

      <Space h="sm" />

      {documentImporterConfiguration.attributes.includes(
        SupportedDocumentAttributes.DISTRIBUTION_DATES
      ) && (
        <Group grow>
          <DatePicker
            id="payslips-modal-distributiondate-input"
            placeholder="Select a distribution date"
            label={
              <InputLabel
                label={
                  Dictionary[documentImporterConfiguration.document_type]
                    .distribution_date
                }
                tooltip={
                  Dictionary[documentImporterConfiguration.document_type]
                    .distribution_date_tooltip
                }
              />
            }
            required
            value={distributionDate}
            dayStyle={(date) =>
              date.getTime() === new Date().setHours(0, 0, 0, 0) &&
              !distributionDate
                ? {
                    backgroundColor: theme.colors[theme.primaryColor][6],
                    color: "white",
                  }
                : {}
            }
            onChange={(value) => setDistributionDate(value)}
          />
          <TimeInput
            placeholder="Select a distribution time"
            label={
              <InputLabel
                label={
                  Dictionary[documentImporterConfiguration.document_type]
                    .distribution_time
                }
                tooltip={
                  Dictionary[documentImporterConfiguration.document_type]
                    .distribution_time_tooltip
                }
              />
            }
            required
            disabled={!distributionDate}
            value={distributionDate}
            onChange={(value) => {
              setDistributionDate((oldDate) => {
                const newDate = oldDate;
                newDate?.setHours(dayjs(value).hour());
                newDate?.setMinutes(dayjs(value).minute());
                return newDate;
              });
            }}
          />
        </Group>
      )}

      <Space h="sm" />

      <FeatureProtectedComponent
        requiredFeature={FEATURE_SELF_MANAGED_PAYSLIPS_MATCHING_CRITERIA}
      >
        {mode === "pdf" && documentMatchingCriteria && (
          <Group pb={38}>
            <Title order={6} pt={24} style={{ fontWeight: 500 }}>
              Matching Criteria{" "}
              <CustomTooltip
                label={
                  Dictionary[documentImporterConfiguration.document_type]
                    .matching_criteria_tooltip
                }
              />
            </Title>
            <Stack>
              <Text style={{ fontSize: "12px", color: "#06213380" }} pl={8}>
                {
                  Dictionary[documentImporterConfiguration.document_type]
                    .matching_criteria_description
                }
              </Text>
              {selectedMatchCriteria?.matching_criteria.length === 0 && (
                <Alert icon={<Info size={16} />} color="red" radius="md">
                  Select at least one matching criteria
                </Alert>
              )}
              {documentMatchingCriteria ? (
                <CheckboxGroup
                  pl={16}
                  value={
                    (selectedMatchCriteria?.matching_criteria.length &&
                      selectedMatchCriteria?.matching_criteria.split(",")) ||
                    []
                  }
                  orientation="vertical"
                  required
                  onChange={updateSelectedMatchingCriteria}
                >
                  {Object.keys(matchingCriteriaDictionary).map(
                    (key: string) => (
                      <Checkbox
                        key={key}
                        value={key}
                        label={matchingCriteriaDictionary[key]}
                      />
                    )
                  )}
                </CheckboxGroup>
              ) : (
                <ProgressIndicator />
              )}
            </Stack>
            <Stack>
              <Text
                style={{ fontSize: "12px", color: "#06213380" }}
                pl={8}
                mt={16}
              >
                What is the page length of each employee{" "}
                {documentImporterConfiguration.document_name_lower_case}?
              </Text>
              <NumberInput
                pl={16}
                min={1}
                value={selectedMatchCriteria?.document_length || 1}
                onChange={updateSelectedMatchingDocumentLength}
              />
            </Stack>
          </Group>
        )}
      </FeatureProtectedComponent>

      {showNotificationCheckBox && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "32px",
          }}
        >
          <Text
            style={{
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "20px",
            }}
          >
            Send Notifications to Employees
          </Text>
          <Switch
            checked={shouldSendNotifications}
            onChange={(value) => {
              trackDocumentsEvent(
                {
                  event: `Send_Notifications_Toggle`,
                  description:
                    "User toggles on the notification option on the upload view",
                  event_type: "Toggle",
                  type: documentImporterConfiguration.document_name_plural,
                  job: data || null,
                },
                {
                  batch_name: batchName,
                  notification_sent_status: value.target.checked,
                }
              );
              setShouldSendNotifications(value.target.checked);
            }}
            size="sm"
          />
        </div>
      )}

      <Space h="xl" />

      <div
        className={classes.btnWrapper}
        style={
          UserAccessControlUtils.hasFeature(
            auth.user_features,
            FEATURE_SELF_MANAGED_PAYSLIPS_MATCHING_CRITERIA
          )
            ? {}
            : {
                position: "sticky",
                bottom: "0",
                left: "0",
                padding: "0 24px 10px 24px",
              }
        }
      >
        <ButtonProgress
          buttonLabel={
            mode === "zip"
              ? "Upload payslips"
              : `Upload ${documentImporterConfiguration.document_name}`
          }
          disabled={!canUpload()}
          onClick={() => {
            trackDocumentsEvent(
              {
                event: `Upload_${documentImporterConfiguration.document_name_plural}_Form_CTA`,
                description:
                  "User clicks on the final upload CTA option on the upload view",
                event_type: "CTA:Click",
                type: documentImporterConfiguration.document_name_plural,
                job: data || null,
              },
              {
                batch_name: batchName,
                notification_sent_status: shouldSendNotifications,
                document_status: "Uploading",
                document_match_rate: 0,
                document_manual_match_count: 0,
                document_manual_match_rate: 0,
                upload_mode: mode,
              }
            );
            setUploadingClicked(true);
            // Belt-and-braces: only run the ZIP flow when it's actually
            // enabled for this document type. The toggle is hidden when
            // !zipModeAvailable and a separate effect resets mode to "pdf",
            // but the explicit check here guards against a transient render
            // where stale state lingers.
            if (zipModeAvailable && mode === "zip") {
              if (!issueDate || !distributionDate) return;
              zipInit.mutate({
                batch_name: batchName,
                issue_date: dayjs(issueDate).format("YYYY-MM-DD"),
                date_of_distribution: dayjs(distributionDate).toISOString(),
                should_send_notifications: shouldSendNotifications,
              });
            } else {
              getSignedUrl();
            }
          }}
          progress={uploadProgress}
        />
      </div>
    </Container>
  );
};

export default PayslipUploadForm;
